<template>
    <BModal
        :model-value="modal"
        centered
        scrollable
        size="xl"
        @update:modelValue="updateModal"
        no-close-on-backdrop
        title="Package Preview"
    >
        <div class="p-4 bg-white" style="font-family: 'Segoe UI', Arial, sans-serif;">
            
            <!-- Header Grid -->
            <div class="row mb-4">
                <div class="col-md-4 mb-2">
                    <div class="font-weight-bold small text-dark">Package Name</div>
                    <div class="mt-1">{{ form.name || '-' }}</div>
                </div>
                <div class="col-md-4 mb-2">
                    <div class="font-weight-bold small text-dark">Pax</div>
                    <div class="mt-1">{{ form.pax || '0' }} Pax</div>
                </div>
                <div class="col-md-4 mb-2">
                    <div class="font-weight-bold small text-dark">Duration</div>
                    <div class="mt-1">{{ form.duration_days || '0' }} Days</div>
                </div>
            </div>

            <!-- Program Table -->
            <div class="table-responsive">
                <table class="table table-bordered">
                    <thead class="bg-light">
                        <tr>
                            <th class="py-2" style="width: 50px;">Day</th>
                            <th class="py-2">Name</th>
                            <th class="py-2">Destination From</th>
                            <th class="py-2">Destination To</th>
                            <th class="py-2">Accommodation</th>
                            <th class="py-2">Transportation</th>
                            <th class="py-2">Activity</th>
                            <th class="py-2">Meal</th>
                            <th class="py-2 text-end" style="width: 150px;">Sub Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(prog, idx) in form.programs" :key="idx">
                            <td class="text-center fw-bold">{{ prog.day }}</td>
                            <td>{{ prog.name }}</td>
                            <td>{{ getDestName(prog.from_destination_uuid) }}</td>
                            <td>{{ getDestName(prog.to_destination_uuid) }}</td>
                            <td>
                                <ul class="list-unstyled mb-0 small" v-if="prog.accommodations?.length">
                                    <li v-for="(acc, i) in prog.accommodations" :key="i" class="mb-1">
                                        • {{ acc.name }}
                                        <div class="x-small text-muted ps-2">
                                            {{ acc.room_type }} {{ acc.extra_bed == 1 ? '+ Extra Bed' : '' }}
                                        </div>
                                    </li>
                                </ul>
                                <span v-else class="text-muted small">-</span>
                            </td>
                            <td>
                                <ul class="list-unstyled mb-0 small" v-if="prog.transportations?.length">
                                    <li v-for="(trn, i) in prog.transportations" :key="i">
                                        • {{ trn.name }} ({{ formatRupiah(trn.sub_total || (trn.price * trn.pax)) }})
                                    </li>
                                </ul>
                                <span v-else class="text-muted small">-</span>
                            </td>
                            <td>
                                <ul class="list-unstyled mb-0 small" v-if="prog.activities?.length">
                                    <li v-for="(act, i) in prog.activities" :key="i">
                                        • {{ act.name }} ({{ formatRupiah(act.sub_total || (act.price * act.pax)) }})
                                    </li>
                                </ul>
                                <span v-else class="text-muted small">-</span>
                            </td>
                            <td>
                                <ul class="list-unstyled mb-0 small" v-if="prog.meals?.length">
                                    <li v-for="(meal, i) in prog.meals" :key="i">
                                        • {{ meal.name }} ({{ formatRupiah(meal.sub_total || (meal.price * (meal.pax || form.pax))) }})
                                    </li>
                                </ul>
                                <span v-else class="text-muted small">-</span>
                            </td>
                            <td class="text-end fw-bold">
                                {{ formatRupiah(getDaySubtotal(prog)) }}
                            </td>
                        </tr>
                        
                        <tr v-if="form.programs.length" class="font-weight-bold">
                             <td colspan="7" class="text-end border-0 pt-3">
                                <span class="text-right text-muted italic small">Grand Total:</span>
                            </td>
                            <td colspan="2" class="text-right mb-0" style="background-color: #e1eed9;">
                                {{ formatRupiah(grandTotal) }}
                            </td>
                        </tr>
                        <tr v-if="!form.programs.length">
                            <td colspan="9" class="text-center py-3 text-muted">No program data available</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Inclusions / Exclusions -->
            <div class="row mt-4">
                <div class="col-md-6">
                    <h6 class="fw-bold mb-2 border-bottom pb-2">Inclusions</h6>
                    <ul v-if="form.inclusion" class="list-unstyled small ps-2">
                        <li v-for="(item, index) in form.inclusion.split('\n')" :key="index" class="mb-1">
                            • {{ item }}
                        </li>
                    </ul>
                    <p v-else class="text-muted small">-</p>
                </div>
                <div class="col-md-6">
                    <h6 class="fw-bold border-bottom pb-2 mb-2">Exclusions</h6>
                    <ul v-if="form.exclusion" class="list-unstyled small ps-2">
                        <li v-for="(item, index) in form.exclusion.split('\n')" :key="index" class="mb-1">
                            • {{ item }}
                        </li>
                    </ul>
                    <p v-else class="text-muted small">-</p>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="d-flex justify-content-end w-100">
                <BButton variant="secondary" class="me-2" @click="updateModal(false)">Close</BButton>
                <BButton variant="primary" @click="confirm">
                    <i class="fas fa-check-circle me-1"></i> {{ isEdit ? 'Confirm & Update' : 'Confirm & Save' }}
                </BButton>
            </div>
        </template>
    </BModal>
</template>

<script setup>
import { defineProps, defineEmits, computed } from "vue";
import { formatRupiah } from "@/utils/format";
import { BModal, BButton } from "bootstrap-vue-next";

const props = defineProps({
    modal: Boolean,
    form: Object,
    destinations: Array,
    isEdit: Boolean
});

const emit = defineEmits(["update:modal", "confirm"]);

const updateModal = (val) => emit("update:modal", val);
const confirm = () => {
    emit("update:modal", false);
    emit("confirm");
};

const getDestName = (uuid) => {
    const d = props.destinations.find(dest => dest.uuid === uuid);
    return d ? d.name : '-';
};

const getDaySubtotal = (day) => {
    let total = 0;
    total += (day.transportations?.reduce((s, i) => s + Number(i.sub_total || (i.price * i.pax) || 0), 0) || 0);
    total += (day.accommodations?.reduce((s, i) => s + Number(i.total_price || (i.price * i.pax) || 0), 0) || 0);
    total += (day.activities?.reduce((s, i) => s + Number(i.sub_total || (i.price * i.pax) || 0), 0) || 0);
    total += (day.meals?.reduce((s, i) => s + Number(i.sub_total || (i.price * (i.pax || props.form.pax)) || 0), 0) || 0);
    return total;
};

const grandTotal = computed(() => {
    return props.form.programs.reduce((acc, day) => acc + getDaySubtotal(day), 0);
});
</script>

<style scoped>
.italic { font-style: italic; }
.x-small { font-size: 0.7rem; }
.small { font-size: 0.85rem; }
.truncate-text {
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
